"use strict";
/**
 * @license
 * Copyright 2017 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  initializeLayerFromSpecShowErrorStatus,
  ManagedUserLayer
} from "#src/layer/index.js";
import { popDragStatus, pushDragStatus } from "#src/ui/drag_and_drop.js";
import {
  decodeParametersFromDragTypeList,
  encodeParametersAsDragType,
  getDropEffect,
  setDropEffect
} from "#src/util/drag_and_drop.js";
import {
  parseArray,
  verifyBoolean,
  verifyObjectProperty,
  verifyString
} from "#src/util/json.js";
const layerDragTypePrefix = "neuroglancer-layer\0";
let dragSource;
export function startLayerDrag(event, sourceInfo) {
  event.dataTransfer.setData(
    encodeParametersAsDragType(
      layerDragTypePrefix,
      sourceInfo.layers.map((layer) => ({
        name: layer.name,
        visible: layer.visible
      }))
    ),
    JSON.stringify({
      layers: sourceInfo.layers.map((layer) => layer.toJSON()),
      layout: sourceInfo.layoutSpec
    })
  );
  if (dragSource !== void 0) {
    dragSource.disposer();
  }
  let newDragSource;
  const disposer = () => {
    sourceInfo.manager.unregisterDisposer(disposer);
    for (const layer of sourceInfo.layers) {
      layer.dispose();
    }
    sourceInfo.manager.dispose();
    if (dragSource === newDragSource) {
      dragSource = void 0;
    }
  };
  dragSource = newDragSource = {
    manager: sourceInfo.manager.addRef(),
    layers: sourceInfo.layers.map((x) => x.addRef()),
    layoutSpec: sourceInfo.layoutSpec,
    isLayerListPanel: sourceInfo.isLayerListPanel ?? false,
    disposer
  };
}
export function endLayerDrag(dropEffect = "none") {
  if (dragSource !== void 0) {
    if (dropEffect === "move") {
      const removedLayers = new Set(dragSource.layers);
      dragSource.manager.layerManager.filter(
        (x) => !removedLayers.has(x)
      );
    }
    dragSource.disposer();
  }
}
export function getLayerDragInfo(event) {
  return decodeParametersFromDragTypeList(
    event.dataTransfer.types,
    layerDragTypePrefix
  );
}
function getCompatibleDragSource(manager) {
  if (dragSource !== void 0 && dragSource.manager.rootLayers === manager.rootLayers) {
    return dragSource;
  }
  return void 0;
}
export class DropLayers {
  /**
   * Called in the 'drop' event handler to actually initialize the layers if they are external.
   * Returns false if any layers failed to initialized.
   */
  initializeExternalLayers(event) {
    const { dragType } = this;
    if (dragType !== void 0) {
      try {
        const { layers: spec, layout } = JSON.parse(
          event.dataTransfer.getData(dragType)
        );
        if (!Array.isArray(spec) || this.numSourceLayers !== spec.length) {
          throw new Error("Invalid layer drop data");
        }
        this.layoutSpec = layout;
        for (const [layer, index] of this.layers) {
          initializeLayerFromSpecShowErrorStatus(layer, spec[index]);
        }
      } catch {
        return false;
      }
    }
    return true;
  }
  updateArchiveStates(event) {
    const { targetIsLayerListPanel } = this;
    const dropEffect = event.dataTransfer.dropEffect;
    for (const layer of this.layers.keys()) {
      let shouldBeArchived = targetIsLayerListPanel;
      if (targetIsLayerListPanel && !layer.archived && dropEffect !== "copy") {
        if (this.sourceIsLayerListPanel) {
          shouldBeArchived = false;
        }
      }
      if (layer.archived !== shouldBeArchived || shouldBeArchived && layer.visible) {
        layer.archived = shouldBeArchived;
        if (shouldBeArchived) layer.visible = false;
        layer.layerChanged.dispatch();
      }
    }
  }
  get method() {
    if (this.sourceManager !== void 0) {
      if (this.manager === this.sourceManager && this.sourceIsLayerListPanel === this.targetIsLayerListPanel) {
        return "move";
      }
      return "link";
    }
    return "copy";
  }
  compatibleWithMethod(otherMethod) {
    if (this.method === otherMethod) {
      return true;
    }
    if (this.forceCopy && otherMethod !== "copy") {
      return false;
    }
    if (!this.moveSupported && otherMethod === "move") {
      return true;
    }
    return false;
  }
}
export function getDropEffectFromModifiers(event, defaultDropEffect, moveAllowed) {
  let dropEffect;
  if (event.shiftKey) {
    dropEffect = "copy";
  } else if (event.ctrlKey && moveAllowed) {
    dropEffect = "move";
  } else {
    dropEffect = defaultDropEffect;
  }
  let message = "";
  const addMessage = (msg) => {
    if (message !== "") {
      message += ", ";
    }
    message += msg;
  };
  if (defaultDropEffect !== "none" && dropEffect !== defaultDropEffect) {
    if (event.shiftKey) {
      addMessage(`release SHIFT to ${defaultDropEffect}`);
    } else {
      addMessage(`release CONTROL to ${defaultDropEffect}`);
    }
  }
  if (dropEffect !== "copy") {
    addMessage("hold SHIFT to copy");
  }
  if (dropEffect !== "move" && moveAllowed && defaultDropEffect !== "move") {
    addMessage("hold CONTROL to move");
  }
  return { dropEffect, dropEffectMessage: message };
}
export function getLayerDropEffect(event, manager, targetIsLayerListPanel, newTarget) {
  const source = getCompatibleDragSource(manager);
  let moveAllowed = false;
  let defaultDropEffect;
  if (source === void 0) {
    defaultDropEffect = "copy";
  } else {
    if (newTarget) {
      if (!source.isLayerListPanel) {
        moveAllowed = true;
      }
      defaultDropEffect = "link";
    } else {
      if (source.manager === manager && source.isLayerListPanel === targetIsLayerListPanel) {
        defaultDropEffect = "move";
        moveAllowed = true;
      } else if (targetIsLayerListPanel) {
        defaultDropEffect = "none";
      } else if (source.isLayerListPanel) {
        defaultDropEffect = "link";
      } else {
        moveAllowed = true;
        defaultDropEffect = "link";
      }
    }
  }
  return getDropEffectFromModifiers(event, defaultDropEffect, moveAllowed);
}
export function updateLayerDropEffect(event, manager, targetIsLayerListPanel, newTarget) {
  const result = getLayerDropEffect(
    event,
    manager,
    targetIsLayerListPanel,
    newTarget
  );
  setDropEffect(event, result.dropEffect);
  return result;
}
export function getDropLayers(event, manager, options) {
  const { forceCopy, newTarget, isLayerListPanel = false } = options;
  const source = getCompatibleDragSource(manager);
  if (!forceCopy && source !== void 0) {
    const moveSupported = !newTarget && source.manager === manager && (source.isLayerListPanel === isLayerListPanel || source.isLayerListPanel);
    const result = new DropLayers();
    result.manager = manager;
    result.numSourceLayers = source.layers.length;
    result.sourceManager = source.manager;
    result.targetIsLayerListPanel = isLayerListPanel;
    result.sourceIsLayerListPanel = source.isLayerListPanel;
    result.moveSupported = moveSupported;
    result.layers = /* @__PURE__ */ new Map();
    result.forceCopy = false;
    result.layoutSpec = source.layoutSpec;
    if (moveSupported) {
      source.layers.forEach((layer, index) => {
        result.layers.set(layer, index);
      });
    } else {
      source.layers.forEach((layer, index) => {
        if (newTarget || !manager.layerManager.has(layer)) {
          result.layers.set(layer.addRef(), index);
        }
      });
    }
    return result;
  }
  const info = getLayerDragInfo(event);
  if (info !== void 0) {
    try {
      const layers = parseArray(info.parameters, (layerInfo, index) => {
        const name = verifyObjectProperty(layerInfo, "name", verifyString);
        let visible = verifyObjectProperty(layerInfo, "visible", verifyBoolean);
        const newLayer = new ManagedUserLayer(name, manager);
        if (isLayerListPanel) visible = false;
        newLayer.visible = visible;
        newLayer.archived = isLayerListPanel;
        return [newLayer, index];
      });
      const result = new DropLayers();
      result.numSourceLayers = layers.length;
      result.targetIsLayerListPanel = isLayerListPanel;
      result.sourceIsLayerListPanel = false;
      result.sourceManager = void 0;
      result.moveSupported = false;
      result.forceCopy = source !== void 0;
      result.manager = manager;
      result.dragType = info.dragType;
      result.layers = new Map(layers);
      return result;
    } catch {
    }
  }
  return void 0;
}
function destroyDropLayers(dropLayers, targetLayer) {
  if (dropLayers.moveSupported) {
    return false;
  }
  dropLayers.manager.layerManager.filter(
    (layer) => !dropLayers.layers.has(layer)
  );
  return targetLayer !== void 0 && dropLayers.layers.has(targetLayer);
}
export function registerLayerBarDropHandlers(panel, target, targetLayer, isLayerListPanel = false) {
  function update(event, updateDropEffect) {
    let dropLayers = panel.dropLayers;
    const { dropEffect, dropEffectMessage } = updateDropEffect ? getLayerDropEffect(
      event,
      panel.manager,
      isLayerListPanel,
      /*newTarget=*/
      false
    ) : {
      dropEffect: getDropEffect(),
      dropEffectMessage: ""
    };
    if (dropEffect === void 0) return void 0;
    setDropEffect(event, dropEffect);
    let existingDropLayers = true;
    if (dropLayers !== void 0) {
      if (!dropLayers.compatibleWithMethod(dropEffect)) {
        panel.dropLayers = void 0;
        if (destroyDropLayers(dropLayers, targetLayer)) {
          return void 0;
        }
      }
    }
    if (dropLayers === void 0) {
      dropLayers = panel.dropLayers = getDropLayers(event, panel.manager, {
        forceCopy: dropEffect === "copy",
        newTarget: false,
        isLayerListPanel
      });
      if (dropLayers === void 0) {
        return void 0;
      }
      existingDropLayers = dropLayers.method === "move";
    }
    if (targetLayer !== void 0 && dropLayers.layers.has(targetLayer)) {
      return { dropLayers, dropEffect, dropEffectMessage };
    }
    if (!existingDropLayers) {
      let newIndex;
      if (targetLayer !== void 0) {
        newIndex = panel.manager.layerManager.managedLayers.indexOf(targetLayer);
      }
      for (const newLayer of dropLayers.layers.keys()) {
        panel.manager.add(newLayer, newIndex);
      }
    } else {
      const { layerManager } = panel.manager;
      const existingLayers = /* @__PURE__ */ new Set();
      let firstRemovalIndex = Number.POSITIVE_INFINITY;
      const managedLayers = layerManager.managedLayers = layerManager.managedLayers.filter((x, index) => {
        if (dropLayers.layers.has(x)) {
          if (firstRemovalIndex === Number.POSITIVE_INFINITY) {
            firstRemovalIndex = index;
          }
          existingLayers.add(x);
          return false;
        }
        return true;
      });
      let newIndex;
      if (targetLayer !== void 0) {
        newIndex = managedLayers.indexOf(targetLayer);
        if (firstRemovalIndex <= newIndex) {
          ++newIndex;
        }
      } else {
        newIndex = managedLayers.length;
      }
      for (const layer of dropLayers.layers.keys()) {
        if (!existingLayers.has(layer)) {
          dropLayers.layers.delete(layer);
        }
      }
      managedLayers.splice(newIndex, 0, ...dropLayers.layers.keys());
      layerManager.layersChanged.dispatch();
    }
    return { dropLayers, dropEffect, dropEffectMessage };
  }
  target.addEventListener("dragenter", (event) => {
    if (update(
      event,
      /*updateDropEffect=*/
      true
    ) !== void 0) {
      event.preventDefault();
    } else {
      popDragStatus(panel.element, "drop");
    }
  });
  target.addEventListener("drop", (event) => {
    event.preventDefault();
    panel.dragEnterCount = 0;
    popDragStatus(panel.element, "drop");
    const dropLayers = update(
      event,
      /*updateDropEffect=*/
      false
    )?.dropLayers;
    panel.dropLayers = void 0;
    if (dropLayers === void 0) return;
    if (!dropLayers.initializeExternalLayers(event)) {
      destroyDropLayers(dropLayers);
      return;
    }
    dropLayers.updateArchiveStates(event);
    endLayerDrag(
      dropLayers.method === "move" ? void 0 : event.dataTransfer.dropEffect
    );
  });
  target.addEventListener("dragover", (event) => {
    const updateResult = update(
      event,
      /*updateDropEffect=*/
      true
    );
    if (updateResult === void 0) {
      popDragStatus(panel.element, "drop");
      return;
    }
    const { dropLayers, dropEffect, dropEffectMessage } = updateResult;
    const numLayers = dropLayers.layers.size;
    let message = "";
    const maybePlural = dropLayers.numSourceLayers === 1 ? "" : "s";
    const numSourceLayers = dropLayers.numSourceLayers;
    if (dropEffect === "none") {
      message = `Cannot link dragged layer${maybePlural} here`;
    } else {
      const layerCountMessage = numSourceLayers === numLayers ? `${numSourceLayers}` : `${numLayers}/${numSourceLayers}`;
      message = `Drop to ${dropEffect} ${layerCountMessage} layer${maybePlural}`;
    }
    if (dropEffectMessage) {
      message += ` (${dropEffectMessage})`;
    }
    pushDragStatus(panel.element, "drop", message);
    event.preventDefault();
    event.stopPropagation();
  });
}
export function registerLayerDragHandlers(panel, element, layer, options) {
  element.draggable = true;
  element.addEventListener("dragstart", (event) => {
    pushDragStatus(
      element,
      "drag",
      "Drag layer to another layer bar/panel (including in another Neuroglancer window), or to the left/top/right/bottom edge of a layer group"
    );
    startLayerDrag(event, {
      manager: panel.manager,
      layers: [layer],
      layoutSpec: options.getLayoutSpec(),
      isLayerListPanel: options.isLayerListPanel
    });
    event.stopPropagation();
  });
  element.addEventListener("dragend", () => {
    popDragStatus(element, "drag");
    endLayerDrag();
  });
}
export function registerLayerBarDragLeaveHandler(panel) {
  panel.element.addEventListener("dragenter", () => {
    ++panel.dragEnterCount;
  });
  panel.element.addEventListener("dragleave", () => {
    if (--panel.dragEnterCount !== 0) return;
    popDragStatus(panel.element, "drop");
    const { dropLayers } = panel;
    if (dropLayers !== void 0) {
      destroyDropLayers(dropLayers);
      panel.manager.layerManager.layersChanged.dispatch();
      panel.dropLayers = void 0;
    }
  });
}
